import React from 'react';

import { Eye, EyeOff } from 'lucide-react';

import { Invitation, InvitationWizardData } from '../../../types/invitation';

interface Step1PersonalInfoProps {
  wizardData: InvitationWizardData;
  invitation?: Invitation;
  errors: Record<string, string>;
  showPassword: boolean;
  showConfirmPassword: boolean;
  onInputChange: (field: string, value: string) => void;
  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;
}

const baseInput =
  'w-full bg-neutral-900 border text-base text-white rounded-lg px-3 py-2 outline-none transition-colors placeholder:text-white/30';

const inputClass = (hasError: boolean) =>
  `${baseInput} ${hasError ? 'border-[#FF8A00]' : 'border-neutral-700 focus:border-green01'}`;

export const Step1PersonalInfo: React.FC<Step1PersonalInfoProps> = ({
  wizardData,
  invitation,
  errors,
  showPassword,
  showConfirmPassword,
  onInputChange,
  onTogglePassword,
  onToggleConfirmPassword,
}) => {
  return (
    <div className="flex flex-col gap-3 md:gap-3.5 mx-4">
      {/* Full Name */}
      <div>
        <label className="block text-xs text-white/70 mb-1">Full Name</label>
        <input
          type="text"
          value={wizardData.fullName}
          onChange={(e) => onInputChange('fullName', e.target.value)}
          className={inputClass(!!errors.fullName)}
        />
        {errors.fullName && <p className="text-[#FF8A00] text-xs mt-1 ml-0.5">{errors.fullName}</p>}
      </div>

      {/* Email (disabled) */}
      <div>
        <label className="block text-xs text-white/70 mb-1">Email</label>
        <input
          type="email"
          value={invitation?.email || ''}
          disabled
          placeholder={invitation?.email || 'Loading...'}
          className={`${baseInput} border-neutral-700 text-greyP2 cursor-not-allowed`}
        />
      </div>

      {/* Password */}
      <div>
        <label className="block text-xs text-white/70 mb-1">Password</label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={wizardData.password}
            onChange={(e) => onInputChange('password', e.target.value)}
            className={`${inputClass(!!errors.password)} pr-10`}
          />
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <p className={`text-xs mt-1 ml-0.5 ${errors.password ? 'text-[#FF8A00]' : 'text-white/50'}`}>
          {errors.password || 'Password must be at least 8 characters'}
        </p>
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-xs text-white/70 mb-1">Confirm Password</label>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={wizardData.confirmPassword}
            onChange={(e) => onInputChange('confirmPassword', e.target.value)}
            className={`${inputClass(!!errors.confirmPassword)} pr-10`}
          />
          <button
            type="button"
            onClick={onToggleConfirmPassword}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {errors.confirmPassword && <p className="text-[#FF8A00] text-xs mt-1 ml-0.5">{errors.confirmPassword}</p>}
      </div>
    </div>
  );
};
